import { ELEMENT_NAMES, WORLD_ELEMENT_TYPES } from "../assets/elements.js";
import { tile } from "./tile.js";
import { mulberry32 } from "../utils/prng.mjs";
import { apiClient } from "../api/apiClient.js";

const WORLD_SIZE_SCALE = 16;

export class tileHandler {
  constructor(worldId, worldSeed, elements, tier = 1) {

    this.worldId = worldId;
    this.worldSeed = worldSeed;
    this.elements = WORLD_ELEMENT_TYPES["all"];
    if (elements[0] !== "all") {
      this.elements = elements
    }
    
    this.tier = tier;
    
    this.worldSize = tier * WORLD_SIZE_SCALE ;

    this.modifiedTiles = new Map();
    this.activeQuests = new Map();

    this.questTypes = ["hunting-amount", "hunting-elemental"];
    this.elementGrid = null;
    

    this.initWorldType();
  }

  static fromData(worldData) {
    const instance = new tileHandler(
      worldData.worldId,
      worldData.worldSeed,
      worldData.element || "all",
      worldData.tier || 1
    );
    instance.loadFromData(worldData);
    return instance;
  }

  initWorldType() {
    this.elementGrid = [];
    this.initElementGrid();
  }

  hasActiveQuests() {
    return this.activeQuests.size > 0;
  }

  initElementGrid() {
    const size = 5;
    const totalCells = size * size;
    const gridRoll = mulberry32(this.worldSeed);

    let flatGrid = [];

    // 1. Distribute full, even sets of elements as long as they fit
    while (flatGrid.length + this.elements.length <= totalCells) {
      flatGrid.push(...this.elements);
    }

    // 2. Fill whatever few cells remain with random elements
    while (flatGrid.length < totalCells) {
      const randomIndex = Math.floor(gridRoll() * this.elements.length);
      flatGrid.push(this.elements[randomIndex]);
    }

    // 3. Shuffle the array so the distribution is randomized (Fisher-Yates)
    for (let i = flatGrid.length - 1; i > 0; i--) {
      const j = Math.floor(gridRoll() * (i + 1));
      [flatGrid[i], flatGrid[j]] = [flatGrid[j], flatGrid[i]];
    }

    // 4. Convert the flat, shuffled array back into the 2D grid
    this.elementGrid = [];
    let index = 0;
    
    for (let i = 0; i < size; i++) {
      this.elementGrid[i] = [];
      for (let j = 0; j < size; j++) {
        this.elementGrid[i][j] = flatGrid[index++];
      }
    }
  }

  removeQuestFromMap(tile) {
    if (tile.currentQuest) {
      this.activeQuests.delete(tile.currentQuest.id);
    }
  }

  killMonster(currentTile) {
    this.activeQuests.forEach((tile) => {
      if (tile.currentQuest.element === currentTile.stats.element && tile.questStatus === "accepted") {
        tile.currentQuest.killProgress++;
        if (tile.currentQuest.killProgress >= tile.currentQuest.killAmount) {
          tile.questStatus = "complete";
        }
      }
    });
  }

  acceptQuest(tile) {
    if (tile.currentQuest && tile.questStatus === "not-accepted") {
      const tileKey = `${tile.xPos},${tile.yPos}`;
      this.modifiedTiles.set(tileKey, tile);
      this.activeQuests.set(tile.currentQuest.id, tile);
      tile.questStatus = "accepted";
    }
  }

  async claimQuestReward(tileID) {
    const tile = this.activeQuests.get(tileID);

    if (!tile || tile.questStatus !== "complete") {
      console.log("Cannot claim reward: Quest not found, incomplete, or already claiming.");
      return null;
    }
    tile.questStatus = "claiming";

    const questReward = structuredClone(tile.currentQuest.reward);

    try {
      const generatedSpells = await apiClient.generateSpell(questReward);

      this.removeQuestFromMap(tile);
      tile.updateQuest();

      const tileKey = `${tile.xPos},${tile.yPos}`;
      // If untouched after cycling the quest, drop it from memory
      if (tile.questStatus === "not-accepted" && tile.prngOffset === 1) {
        this.modifiedTiles.delete(tileKey);
      }

      return generatedSpells;
    } catch (error) {
      console.error("Failed to generate spell from reward. Rolling back state:", error);
      tile.questStatus = "complete";
      throw new Error("Server unreachable. Reward progress preserved. Please try again.");
    }
  }

  generateTile(xPos, yPos) {
    return new tile(xPos, yPos, this.worldSeed, this.elementGrid, this.tier);
  }

  getTile(xPos, yPos) {
    if (Math.abs(xPos) > this.worldSize || Math.abs(yPos) > this.worldSize) {
      return "empty";
    }

    const tileKey = `${xPos},${yPos}`;

    if (this.modifiedTiles.has(tileKey)) {
      return this.modifiedTiles.get(tileKey);
    }

    return this.generateTile(xPos, yPos);
  }

  saveToData() {
    const serializedModifiedTiles = [];

    for (const [key, tileInstance] of this.modifiedTiles.entries()) {
      const json = tileInstance.toJSON();
      if (json) {
        serializedModifiedTiles.push([key, json]);
      }
    }

    return {
      worldId: this.worldId,
      worldSeed: this.worldSeed,
      elements: this.elements,
      tier: this.tier,
      modifiedTiles: serializedModifiedTiles,
      activeQuests: Array.from(this.activeQuests.keys())
    };
  }

  loadFromData(data) {
    if (!data) return;

    this.worldId = data.worldId;
    this.worldSeed = data.worldSeed;
    this.elements = data.elements || "all";
    this.tier = data.tier || 1;
    this.worldSize = this.tier * WORLD_SIZE_SCALE;
    

    this.initWorldType();

    this.modifiedTiles = new Map();
    this.activeQuests = new Map();

    // 1. Rebuild modified tiles
    if (data.modifiedTiles) {
      data.modifiedTiles.forEach(([key, rawData]) => {
        const [
          xPos,
          yPos,
          prngOffset,
          questStatus,
          element,
          tier,
          capacity,
          strengthLevel,
          sanctuaryLevel,
          sanctuaryBoost,
          killProgress
        ] = rawData;

        // Pass isLoading = true to prevent double tile-generation in constructor
        const reconstructedTile = new tile(xPos, yPos, this.worldSeed, this.elementGrid, this.tier, true);

        reconstructedTile.prngOffset = prngOffset - 1;
        reconstructedTile.generateTile(this.elementGrid);

        reconstructedTile.stats.element = element;
        reconstructedTile.stats.tier = tier;
        reconstructedTile.stats.capacity = capacity;
        reconstructedTile.stats.strengthLevel = strengthLevel;
        reconstructedTile.stats.sanctuaryLevel = sanctuaryLevel;
        reconstructedTile.stats.sanctuaryBoost = sanctuaryBoost;
        reconstructedTile.questStatus = questStatus;

        if (reconstructedTile.currentQuest) {
          reconstructedTile.currentQuest.killProgress = killProgress || 0;
        }

        this.modifiedTiles.set(key, reconstructedTile);
      });
    }

    // 2. Re-bind active quest instances directly from modifiedTiles
    if (data.activeQuests) {
      data.activeQuests.forEach((questId) => {
        for (const tileInstance of this.modifiedTiles.values()) {
          if (tileInstance.currentQuest && tileInstance.currentQuest.id === questId) {
            this.activeQuests.set(questId, tileInstance);
            break;
          }
        }
      });
    }
  }
}